import * as api from './api';
import { decryptWithMasterKey, encryptWithMasterKey } from './crypto';
import { getMasterKey } from './session';
import { getAllConversations, saveConversation, type ConversationRecord } from '../db/conversations-repo';
import { saveOptimizationSettings } from '../db/settings-repo';
import type { BenchmarkStats } from '../db/benchmarks-repo';
import type { OptimizationSettings } from '../pipeline/settings';

/**
 * Pulls settings + conversations from the server and overwrites local copies. Settings have no
 * local timestamp to compare against, so a pull (only triggered right after sign-in) always wins;
 * conversations do carry `updatedAt` and are merged per-record instead.
 */
export async function pullAll(): Promise<void> {
  const [settingsRes, conversationsRes] = await Promise.all([api.getSyncSettings(), api.getSyncConversations()]);

  if (settingsRes.data) {
    const remote = JSON.parse(settingsRes.data) as OptimizationSettings;
    await saveOptimizationSettings(remote);
  }

  const localConversations = await getAllConversations();
  const localById = new Map(localConversations.map((c) => [c.id, c]));
  for (const remote of conversationsRes.conversations) {
    const record = JSON.parse(remote.data) as ConversationRecord;
    const local = localById.get(record.id);
    if (!local || remote.updatedAt >= local.updatedAt) {
      await saveConversation(record);
    }
  }
}

export async function pushSettings(settings: OptimizationSettings): Promise<void> {
  await api.putSyncSettings(settings);
}

export async function pushConversation(record: ConversationRecord): Promise<void> {
  await api.putSyncConversation(record.id, record);
}

/** Encrypts `apiKey` under the in-memory master key K and pushes the ciphertext to the server. */
export async function pushApiKey(apiKey: string): Promise<void> {
  const masterKey = getMasterKey();
  if (!masterKey) throw new Error('Master key is locked — sign in with a passkey to sync your API key.');
  const { ciphertext, iv } = await encryptWithMasterKey(masterKey, apiKey);
  await api.putSyncApiKey(ciphertext, iv);
}

/** Fetches and decrypts the synced API key, or null if none is set or K is locked. */
export async function pullApiKey(): Promise<string | null> {
  const masterKey = getMasterKey();
  if (!masterKey) return null;
  const { ciphertext, iv } = await api.getSyncApiKey();
  if (!ciphertext || !iv) return null;
  return decryptWithMasterKey(masterKey, { ciphertext, iv });
}

/** Encrypts `token` under the in-memory master key K and pushes the ciphertext to the server. */
export async function pushGithubToken(token: string): Promise<void> {
  const masterKey = getMasterKey();
  if (!masterKey) throw new Error('Master key is locked — sign in with a passkey to sync your GitHub token.');
  const { ciphertext, iv } = await encryptWithMasterKey(masterKey, token);
  await api.putSyncGithubToken(ciphertext, iv);
}

/** Fetches and decrypts the synced GitHub token, or null if none is set or K is locked. */
export async function pullGithubToken(): Promise<string | null> {
  const masterKey = getMasterKey();
  if (!masterKey) return null;
  const { ciphertext, iv } = await api.getSyncGithubToken();
  if (!ciphertext || !iv) return null;
  return decryptWithMasterKey(masterKey, { ciphertext, iv });
}

/** Overwrites this device's cumulative benchmark counters on the server with the current local totals. */
export async function pushBenchmarks(stats: BenchmarkStats): Promise<void> {
  await api.putBenchmarks({ ...stats, label: navigator.userAgent.slice(0, 120) });
}

/** This user's per-device benchmark breakdown plus totals summed across all their devices. */
export function pullBenchmarks() {
  return api.getBenchmarks();
}
